import type { SalaryPreference } from "./forms/MinimumPayForm";
import type { InferenceMethod } from "../../../requirements/TaxonomyEntity";

import { getFooterButtons } from "./CollectionModal.util";
import { useI18n } from "../../../../../../../../shared/I18n";
import Sentiment from "../../../../../../../../shared/util/salary/Sentiment";
import { useModalContext } from "../../../context/JMUIModalContextProvider";

import { useEffect, useState } from "react";

export type CollectionModalData = {
  sentiment: Sentiment;
  salaryPreference?: SalaryPreference;
};

export type UseCollectionModalControllerParams = {
  data: CollectionModalData;
  inferenceMethod?: InferenceMethod;
  showSalary?: boolean;
  /**
   * Dismiss the presenting container. The navigation-screen variant passes
   * `navigation.goBack`; the desktop overlay passes its overlay-close.
   */
  closeModal: () => void;
};

/**
 * Shared state + handlers for the Collection modal, used by both the
 * mobile navigation-screen variant (`CollectionModal`) and the desktop
 * overlay variant (`CollectionModalOverlay`) so their save/reset/close
 * behavior cannot drift. Presentation (full-screen vs centered dialog) is
 * left to each caller.
 */
export function useCollectionModalController({
  data,
  inferenceMethod,
  showSalary,
  closeModal,
}: UseCollectionModalControllerParams) {
  const gt = useI18n();
  const { hasSubmitted, setHasSubmitted, collectionModalCallbacksRef } = useModalContext();

  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState(data);
  const [initialData, setInitialData] = useState(data);

  useEffect(() => {
    setModalData(data);
    setInitialData(data);
  }, [data]);

  const { onModalSubmit, onModalReset, onClose } = collectionModalCallbacksRef.current;

  const handleSave = async (sentiment: Sentiment) => {
    let saveStatus = false;

    setHasSubmitted(true);
    setLoading(true);

    saveStatus = await onModalSubmit({ ...modalData, sentiment });
    if (saveStatus) {
      setModalData({ ...modalData, sentiment });
    }

    setLoading(false);

    return saveStatus;
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    closeModal();
  };

  const originalSentiment = initialData?.sentiment || Sentiment.UNKNOWN;
  const sentiment = modalData?.sentiment || Sentiment.UNKNOWN;

  const footerButtons = getFooterButtons({
    sentiment,
    inferenceMethod,
    originalSentiment,
    loading,
    gt,
    onModalReset: async () => {
      const resetStatus = await onModalReset();
      setHasSubmitted(false);
      if (resetStatus) {
        handleClose();
      }
      return resetStatus;
    },
    handleSave,
    showSalary,
    onClose: handleClose,
  });

  return {
    gt,
    loading,
    hasSubmitted,
    modalData,
    setModalData,
    originalSentiment,
    sentiment,
    footerButtons,
    handleSave,
    handleClose,
  };
}
